import { ReducerChain, withScope } from "@gamedev-web/gamedev-sdk-core";
import { TicketStatusCode } from "@gamedev-web/one-gaming-crm";

import {
  DefaultTeamMemberData,
  PaginatedTicketsResponse,
  PartnerCenterSeller,
  TeamDetailResponse,
  TICKET_PAGE_SIZE,
  TicketCategory,
  TicketComment,
  TicketData,
  TicketDetailData,
  TicketDetailResponse,
  TicketingAccessInfo,
  TicketingAccessMode,
  TicketOptionsResponse,
  TicketPaginationState,
  TicketsResponse,
  TicketStatusCounts,
  TicketStatusLabel,
  TicketSubCategory,
  TicketTeamAdminData,
  TicketTeamData,
  TicketTeamsAdminResponse,
  TranslationMap,
  UpdateTicketRequest,
} from "../../models/ticketing";
import { TicketingActions, ticketingActions } from "../actions/ticketingActions";
import { TicketingScope } from "../scopes";

export type TicketingState = {
  isVerified: boolean;
  isVerifying: boolean;
  isVerifyChecked: boolean;
  verifyError: boolean;
  accessMode: TicketingAccessMode;
  sellers: PartnerCenterSeller[];
  selectedAccessId: string | undefined;
  tickets: TicketData[];
  isTicketsFetched: boolean;
  categories: TicketCategory[];
  subCategories: TicketSubCategory[];
  isOptionsFetched: boolean;
  isSubmitting: boolean;
  submitSuccess: boolean;
  submitError: string | undefined;
  selectedTicket: TicketDetailData | undefined;
  isDetailLoading: boolean;
  isAddingComment: boolean;
  isUpdating: boolean;
  loginEmail: string | undefined;
  notificationEmail: string | undefined;
  notificationEmailOptOut: boolean;
  notificationEmailVerified: boolean;
  isNotificationEmailSaving: boolean;
  notificationEmailError: string | undefined;
  isNewContact: boolean;
  translationMap: TranslationMap;
  pagination: TicketPaginationState;
  isPaginatedFetching: boolean;
  statusCounts: TicketStatusCounts | undefined;
  fetchGeneration: number;
  teams: TicketTeamData[];
  teamsAdmin: TicketTeamAdminData[];
  teamsAdminSellerName: string;
  isTeamsAdminFetching: boolean;
  teamsAdminError: string | undefined;
  isTicketingAdmin: boolean | undefined;
  isTicketingManager: boolean | undefined;
  defaultTeamMembers: DefaultTeamMemberData[];
  isDefaultTeamMembersFetching: boolean;
  isCreatingTeam: boolean;
  createdTeamId: string | undefined;
  createTeamError: string | undefined;
  teamDetail: TeamDetailResponse | undefined;
  isTeamDetailFetching: boolean;
  teamDetailError: string | undefined;
  isRemovingMember: boolean;
  isAddingMembers: boolean;
  isRenamingTeam: boolean;
  isAssigningManager: boolean;
  isRemovingManager: boolean;
};

const initialPaginationState: TicketPaginationState = {
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
  pageSize: TICKET_PAGE_SIZE,
  sortBy: "createdon",
  sortDir: "desc",
  filterTypes: [],
  filterSeverities: [],
  search: "",
  queryTokens: {},
};

export const initialTicketingState: TicketingState = {
  isVerified: false,
  isVerifying: false,
  isVerifyChecked: false,
  verifyError: false,
  accessMode: TicketingAccessMode.Denied,
  sellers: [],
  selectedAccessId: undefined,
  tickets: [],
  isTicketsFetched: false,
  categories: [],
  subCategories: [],
  isOptionsFetched: false,
  isSubmitting: false,
  submitSuccess: false,
  submitError: undefined,
  selectedTicket: undefined,
  isDetailLoading: false,
  isAddingComment: false,
  isUpdating: false,
  loginEmail: undefined,
  notificationEmail: undefined,
  notificationEmailOptOut: false,
  notificationEmailVerified: false,
  isNotificationEmailSaving: false,
  notificationEmailError: undefined,
  isNewContact: false,
  translationMap: {},
  pagination: initialPaginationState,
  isPaginatedFetching: false,
  statusCounts: undefined,
  fetchGeneration: 0,
  teams: [],
  teamsAdmin: [],
  teamsAdminSellerName: "",
  isTeamsAdminFetching: false,
  teamsAdminError: undefined,
  isTicketingAdmin: undefined,
  isTicketingManager: undefined,
  defaultTeamMembers: [],
  isDefaultTeamMembersFetching: false,
  isCreatingTeam: false,
  createdTeamId: undefined,
  createTeamError: undefined,
  teamDetail: undefined,
  isTeamDetailFetching: false,
  teamDetailError: undefined,
  isRemovingMember: false,
  isAddingMembers: false,
  isRenamingTeam: false,
  isAssigningManager: false,
  isRemovingManager: false,
};

export const verifyAccessStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isVerifying: true,
  verifyError: false,
});

export const verifyAccessCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.verifyAccess.success>
): TicketingState => {
  const info = action.accessInfo as TicketingAccessInfo;
  // Pre-select first seller
  let selectedAccessId: string | undefined;
  if (info.sellers.length > 0) {
    selectedAccessId = info.sellers[0].id;
  }

  // Override with URL param if it matches a valid seller
  if (typeof window !== "undefined" && selectedAccessId) {
    const urlAccessId = new URLSearchParams(window.location.search).get("accessid");
    if (urlAccessId) {
      const isValidSeller = info.sellers.some(s => s.id === urlAccessId);
      if (isValidSeller) {
        selectedAccessId = urlAccessId;
      }
    }
  }

  return {
    ..._state,
    isVerifying: false,
    isVerifyChecked: true,
    isVerified: info.verified,
    accessMode: info.accessMode,
    sellers: info.sellers,
    selectedAccessId,
    loginEmail: info.loginEmail,
    notificationEmail: info.notificationEmail,
    notificationEmailOptOut: info.notificationEmailOptOut ?? false,
    notificationEmailVerified: info.notificationEmailVerified ?? false,
    isNewContact: info.isNewContact ?? false,
  };
};

export const verifyAccessError = (_state: TicketingState): TicketingState => ({
  ..._state,
  isVerifying: false,
  isVerifyChecked: true,
  verifyError: true,
});

export const updateTickets = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.fetchTickets.success>
): TicketingState => {
  const response = action.ticketsResponse as TicketsResponse;
  return {
    ..._state,
    tickets: response.tickets,
    isTicketsFetched: true,
    translationMap: response.translations ?? _state.translationMap,
  };
};

export const updateOptions = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.fetchOptions.success>
): TicketingState => ({
  ..._state,
  categories: (action.optionsResponse as TicketOptionsResponse).categories,
  subCategories: (action.optionsResponse as TicketOptionsResponse).subCategories,
  translationMap: (action.optionsResponse as TicketOptionsResponse).translations ?? {},
  teams: (action.optionsResponse as TicketOptionsResponse).teams ?? _state.teams,
  isOptionsFetched: true,
});

export const submitTicketStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isSubmitting: true,
  submitSuccess: false,
  submitError: undefined,
});

export const submitTicketCompleted = (_state: TicketingState): TicketingState => ({
  ..._state,
  isSubmitting: false,
  submitSuccess: true,
});

export const submitTicketError = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.submitTicket.error>
): TicketingState => ({
  ..._state,
  isSubmitting: false,
  submitError: (action.error as Error).message,
});

export const clearSubmitStatus = (_state: TicketingState): TicketingState => ({
  ..._state,
  isSubmitting: false,
  submitSuccess: false,
  submitError: "",
});

export const fetchDetailStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  selectedTicket: undefined,
  isDetailLoading: true,
});

export const fetchDetailCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.fetchDetail.success>
): TicketingState => ({
  ..._state,
  selectedTicket: (action.detailResponse as TicketDetailResponse).ticket,
  isDetailLoading: false,
});

export const fetchDetailError = (_state: TicketingState): TicketingState => ({
  ..._state,
  isDetailLoading: false,
});

export const clearDetail = (_state: TicketingState): TicketingState => ({
  ..._state,
  selectedTicket: undefined,
  isDetailLoading: false,
});

export const dismissNewContact = (_state: TicketingState): TicketingState => ({
  ..._state,
  isNewContact: false,
});

export const addCommentStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isAddingComment: true,
});

export const addCommentCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.addComment.success>
): TicketingState => {
  const commentText = action.comment as string;
  const now = new Date();
  const newComment: TicketComment = {
    id: `local-${Date.now()}`,
    comment: commentText,
    authorType: "user",
    authorName: (action.authorName as string) ?? _state.selectedTicket?.submittedBy,
    createdOn: now.toISOString(),
  };
  return {
    ..._state,
    isAddingComment: false,
    selectedTicket: _state.selectedTicket
      ? {
          ..._state.selectedTicket,
          comments: [newComment, ..._state.selectedTicket.comments],
        }
      : undefined,
  };
};

export const addCommentError = (_state: TicketingState): TicketingState => ({
  ..._state,
  isAddingComment: false,
});

const statusCodeLabels: Record<number, string> = {
  [TicketStatusCode.active]: TicketStatusLabel.Active,
  [TicketStatusCode.proposed]: TicketStatusLabel.Proposed,
  [TicketStatusCode.resolved]: TicketStatusLabel.Resolved,
  [TicketStatusCode.closed]: TicketStatusLabel.Closed,
};

export const updateTicketStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isUpdating: true,
});

export const updateTicketCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.updateTicket.success>
): TicketingState => {
  const req = action.request as UpdateTicketRequest;
  if (!_state.selectedTicket) {
    return { ..._state, isUpdating: false };
  }
  let updated = { ..._state.selectedTicket };

  if (req.tags !== undefined) {
    updated = { ...updated, tags: req.tags };
  }
  if (req.statusCode !== undefined) {
    const newStatus = statusCodeLabels[req.statusCode] ?? updated.status;
    updated = { ...updated, status: newStatus };
    // When closing, set the closed date locally
    if (req.statusCode === TicketStatusCode.closed) {
      const now = new Date();
      const month = String(now.getUTCMonth() + 1).padStart(2, "0");
      const day = String(now.getUTCDate()).padStart(2, "0");
      const year = String(now.getUTCFullYear()).slice(-2);
      updated = { ...updated, closedDate: `${month}/${day}/${year}` };
    }
  }
  if (req.removeAttachmentIds && req.removeAttachmentIds.length > 0) {
    const removeSet = new Set(req.removeAttachmentIds);
    updated = {
      ...updated,
      attachments: updated.attachments.filter(a => !removeSet.has(a.id)),
    };
  }
  if (req.addAttachments && req.addAttachments.length > 0) {
    const now = new Date();
    const month = String(now.getUTCMonth() + 1).padStart(2, "0");
    const day = String(now.getUTCDate()).padStart(2, "0");
    const year = now.getUTCFullYear();
    const hours = String(now.getUTCHours()).padStart(2, "0");
    const minutes = String(now.getUTCMinutes()).padStart(2, "0");
    const ts = `${month}/${day}/${year} ${hours}:${minutes}`;
    const newAtts = req.addAttachments.map((att, i) => ({
      id: `local-att-${Date.now()}-${i}`,
      name: att.url,
      description: att.description || "",
      url: att.url,
      createdOn: ts,
    }));
    updated = { ...updated, attachments: [...updated.attachments, ...newAtts] };
  }

  // Also update the ticket in the main list
  const updatedTickets = _state.tickets.map(t => {
    if (t.id !== req.ticketId) {
      return t;
    }
    const changes: Partial<TicketData> = {};
    if (req.statusCode !== undefined) {
      changes.status = statusCodeLabels[req.statusCode] ?? t.status;
      if (req.statusCode === TicketStatusCode.closed) {
        const now = new Date();
        const month = String(now.getUTCMonth() + 1).padStart(2, "0");
        const day = String(now.getUTCDate()).padStart(2, "0");
        const year = String(now.getUTCFullYear()).slice(-2);
        changes.closedDate = `${month}/${day}/${year}`;
      }
    }
    return { ...t, ...changes };
  });

  return { ..._state, isUpdating: false, selectedTicket: updated, tickets: updatedTickets };
};

export const updateTicketError = (_state: TicketingState): TicketingState => ({
  ..._state,
  isUpdating: false,
});

export const setAccessId = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.setAccessId>
): TicketingState => {
  const requestedId = action.accessId as string;
  // Validate against verified sellers — reject if sellers are loaded and ID is not recognized
  if (_state.sellers.length > 0 && !_state.sellers.some(s => s.id === requestedId)) {
    return _state;
  }
  return {
    ..._state,
    selectedAccessId: requestedId,
    tickets: [],
    isTicketsFetched: false,
  };
};

export const updateNotificationEmailStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isNotificationEmailSaving: true,
  notificationEmailError: undefined,
});

export const updateNotificationEmailCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.updateNotificationEmail.success>
): TicketingState => ({
  ..._state,
  isNotificationEmailSaving: false,
  notificationEmail: action.notificationEmail as string,
  notificationEmailVerified: false,
  isNewContact: false,
});

export const updateNotificationEmailError = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.updateNotificationEmail.error>
): TicketingState => ({
  ..._state,
  isNotificationEmailSaving: false,
  notificationEmailError: (action.error as Error).message,
});

export const updateNotificationOptOutStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isNotificationEmailSaving: true,
  notificationEmailError: undefined,
});

export const updateNotificationOptOutCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.updateNotificationOptOut.success>
): TicketingState => ({
  ..._state,
  isNotificationEmailSaving: false,
  notificationEmailOptOut: action.optOut as boolean,
});

export const updateNotificationOptOutError = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.updateNotificationOptOut.error>
): TicketingState => ({
  ..._state,
  isNotificationEmailSaving: false,
  notificationEmailError: (action.error as Error).message,
});

export const fetchPaginatedTicketsStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isPaginatedFetching: true,
  fetchGeneration: _state.fetchGeneration + 1,
});

export const fetchPaginatedTicketsCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.fetchPaginatedTickets.success>
): TicketingState => {
  const { response, generation } = action as unknown as {
    response: PaginatedTicketsResponse;
    generation: number;
  };

  // Ignore stale responses from previous fetch generations
  if (generation !== undefined && generation !== _state.fetchGeneration) {
    return _state;
  }

  // Store the queryToken for the next page (keyed by currentPage + 1)
  const newQueryTokens = { ..._state.pagination.queryTokens };
  if (response.queryToken) {
    newQueryTokens[response.currentPage + 1] = response.queryToken;
  }
  return {
    ..._state,
    tickets: response.tickets,
    isTicketsFetched: true,
    isPaginatedFetching: false,
    translationMap: response.translations ?? _state.translationMap,
    statusCounts: response.statusCounts ?? _state.statusCounts,
    teams: response.teams ?? _state.teams,
    pagination: {
      ..._state.pagination,
      currentPage: response.currentPage,
      totalPages: response.totalPages,
      totalCount: response.totalCount,
      queryTokens: newQueryTokens,
    },
  };
};

export const fetchPaginatedTicketsError = (_state: TicketingState): TicketingState => ({
  ..._state,
  isPaginatedFetching: false,
});

export const setPaginationParams = (
  _state: TicketingState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: any
): TicketingState => {
  const params = action.params as Partial<{
    sortBy: string;
    sortDir: "asc" | "desc";
    filterTypes: number[];
    filterSeverities: number[];
    search: string;
    page: number;
    pageSize: number;
  }>;
  if (!params) {
    return _state;
  }

  // Clear cached tokens when resetting to page 1 or changing query-affecting params
  const shouldClearTokens =
    params.page === 1 ||
    params.sortBy !== undefined ||
    params.sortDir !== undefined ||
    params.filterTypes !== undefined ||
    params.filterSeverities !== undefined ||
    params.search !== undefined;

  const newPagination = {
    ..._state.pagination,
    ...params,
    currentPage: params.page ?? _state.pagination.currentPage,
    queryTokens: shouldClearTokens ? {} : _state.pagination.queryTokens,
  };

  return {
    ..._state,
    pagination: newPagination,
  };
};

export const fetchTeamsAdminStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isTeamsAdminFetching: true,
  teamsAdminError: undefined,
});

export const fetchTeamsAdminCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.fetchTeamsAdmin.success>
): TicketingState => {
  const { response } = action as unknown as { response: TicketTeamsAdminResponse };
  return {
    ..._state,
    isTeamsAdminFetching: false,
    teamsAdmin: response.teams,
    teamsAdminSellerName: response.sellerName || "",
    isTicketingAdmin: response.isAdmin,
    isTicketingManager: response.isManager,
  };
};

export const fetchTeamsAdminError = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.fetchTeamsAdmin.error>
): TicketingState => ({
  ..._state,
  isTeamsAdminFetching: false,
  teamsAdminError: (action.error as Error).message,
  isTicketingAdmin: false,
  isTicketingManager: false,
});

export const fetchDefaultTeamMembersStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isDefaultTeamMembersFetching: true,
});

export const fetchDefaultTeamMembersCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.fetchDefaultTeamMembers.success>
): TicketingState => ({
  ..._state,
  isDefaultTeamMembersFetching: false,
  defaultTeamMembers: (action as unknown as { members: DefaultTeamMemberData[] }).members,
});

export const fetchDefaultTeamMembersError = (_state: TicketingState): TicketingState => ({
  ..._state,
  isDefaultTeamMembersFetching: false,
});

export const createTeamStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isCreatingTeam: true,
  createdTeamId: undefined,
  createTeamError: undefined,
});

export const createTeamCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.createTeam.success>
): TicketingState => ({
  ..._state,
  isCreatingTeam: false,
  createdTeamId: (action as unknown as { teamId: string }).teamId,
});

export const createTeamError = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.createTeam.error>
): TicketingState => ({
  ..._state,
  isCreatingTeam: false,
  createTeamError: (action.error as Error).message,
});

export const fetchTeamDetailStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isTeamDetailFetching: true,
  teamDetailError: undefined,
});

export const fetchTeamDetailCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.fetchTeamDetail.success>
): TicketingState => {
  const { response } = action as unknown as { response: TeamDetailResponse };
  return {
    ..._state,
    isTeamDetailFetching: false,
    teamDetail: response,
  };
};

export const fetchTeamDetailError = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.fetchTeamDetail.error>
): TicketingState => ({
  ..._state,
  isTeamDetailFetching: false,
  teamDetailError: (action.error as Error).message,
});

export const removeTeamMemberStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isRemovingMember: true,
});

export const removeTeamMemberCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.removeTeamMember.success>
): TicketingState => {
  const { contactId } = action as unknown as { contactId: string };
  const teamDetail = _state.teamDetail;
  if (!teamDetail) {
    return { ..._state, isRemovingMember: false };
  }
  return {
    ..._state,
    isRemovingMember: false,
    teamDetail: {
      ...teamDetail,
      members: teamDetail.members.filter(m => m.contactId !== contactId),
      memberCount: teamDetail.memberCount - 1,
    },
  };
};

export const removeTeamMemberError = (_state: TicketingState): TicketingState => ({
  ..._state,
  isRemovingMember: false,
});

export const addTeamMembersStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isAddingMembers: true,
});

export const addTeamMembersCompleted = (_state: TicketingState): TicketingState => ({
  ..._state,
  isAddingMembers: false,
});

export const addTeamMembersError = (_state: TicketingState): TicketingState => ({
  ..._state,
  isAddingMembers: false,
});

export const renameTeamStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isRenamingTeam: true,
});

export const renameTeamCompleted = (
  _state: TicketingState,
  action: ReturnType<typeof ticketingActions.renameTeam.success>
): TicketingState => {
  const { newName } = action as unknown as { newName: string };
  const teamDetail = _state.teamDetail;
  if (!teamDetail) {
    return { ..._state, isRenamingTeam: false };
  }
  return {
    ..._state,
    isRenamingTeam: false,
    teamDetail: { ...teamDetail, teamName: newName },
  };
};

export const renameTeamError = (_state: TicketingState): TicketingState => ({
  ..._state,
  isRenamingTeam: false,
});

export const assignTeamManagerStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isAssigningManager: true,
});

export const assignTeamManagerCompleted = (_state: TicketingState): TicketingState => ({
  ..._state,
  isAssigningManager: false,
});

export const assignTeamManagerError = (_state: TicketingState): TicketingState => ({
  ..._state,
  isAssigningManager: false,
});

export const removeTeamManagerStart = (_state: TicketingState): TicketingState => ({
  ..._state,
  isRemovingManager: true,
});

export const removeTeamManagerCompleted = (_state: TicketingState): TicketingState => ({
  ..._state,
  isRemovingManager: false,
});

export const removeTeamManagerError = (_state: TicketingState): TicketingState => ({
  ..._state,
  isRemovingManager: false,
});

export const ticketingReducer = withScope(
  [TicketingScope],
  new ReducerChain<TicketingState>(initialTicketingState)
    .on(TicketingActions.VERIFY_ACCESS_START, verifyAccessStart)
    .on(TicketingActions.VERIFY_ACCESS_COMPLETED, verifyAccessCompleted)
    .on(TicketingActions.VERIFY_ACCESS_ERROR, verifyAccessError)
    .on(TicketingActions.FETCH_TICKETS_COMPLETED, updateTickets)
    .on(TicketingActions.FETCH_OPTIONS_COMPLETED, updateOptions)
    .on(TicketingActions.SUBMIT_TICKET_START, submitTicketStart)
    .on(TicketingActions.SUBMIT_TICKET_COMPLETED, submitTicketCompleted)
    .on(TicketingActions.SUBMIT_TICKET_ERROR, submitTicketError)
    .on(TicketingActions.CLEAR_SUBMIT_STATUS, clearSubmitStatus)
    .on(TicketingActions.FETCH_DETAIL_START, fetchDetailStart)
    .on(TicketingActions.FETCH_DETAIL_COMPLETED, fetchDetailCompleted)
    .on(TicketingActions.FETCH_DETAIL_ERROR, fetchDetailError)
    .on(TicketingActions.CLEAR_DETAIL, clearDetail)
    .on(TicketingActions.DISMISS_NEW_CONTACT, dismissNewContact)
    .on(TicketingActions.ADD_COMMENT_START, addCommentStart)
    .on(TicketingActions.ADD_COMMENT_COMPLETED, addCommentCompleted)
    .on(TicketingActions.ADD_COMMENT_ERROR, addCommentError)
    .on(TicketingActions.UPDATE_TICKET_START, updateTicketStart)
    .on(TicketingActions.UPDATE_TICKET_COMPLETED, updateTicketCompleted)
    .on(TicketingActions.UPDATE_TICKET_ERROR, updateTicketError)
    .on(TicketingActions.UPDATE_NOTIFICATION_EMAIL_START, updateNotificationEmailStart)
    .on(TicketingActions.UPDATE_NOTIFICATION_EMAIL_COMPLETED, updateNotificationEmailCompleted)
    .on(TicketingActions.UPDATE_NOTIFICATION_EMAIL_ERROR, updateNotificationEmailError)
    .on(TicketingActions.UPDATE_NOTIFICATION_OPT_OUT_START, updateNotificationOptOutStart)
    .on(TicketingActions.UPDATE_NOTIFICATION_OPT_OUT_COMPLETED, updateNotificationOptOutCompleted)
    .on(TicketingActions.UPDATE_NOTIFICATION_OPT_OUT_ERROR, updateNotificationOptOutError)
    .on(TicketingActions.SET_ACCESS_ID, setAccessId)
    .on(TicketingActions.FETCH_PAGINATED_TICKETS_START, fetchPaginatedTicketsStart)
    .on(TicketingActions.FETCH_PAGINATED_TICKETS_COMPLETED, fetchPaginatedTicketsCompleted)
    .on(TicketingActions.FETCH_PAGINATED_TICKETS_ERROR, fetchPaginatedTicketsError)
    .on(TicketingActions.SET_PAGINATION_PARAMS, setPaginationParams)
    .on(TicketingActions.FETCH_TEAMS_ADMIN_START, fetchTeamsAdminStart)
    .on(TicketingActions.FETCH_TEAMS_ADMIN_COMPLETED, fetchTeamsAdminCompleted)
    .on(TicketingActions.FETCH_TEAMS_ADMIN_ERROR, fetchTeamsAdminError)
    .on(TicketingActions.FETCH_DEFAULT_TEAM_MEMBERS_START, fetchDefaultTeamMembersStart)
    .on(TicketingActions.FETCH_DEFAULT_TEAM_MEMBERS_COMPLETED, fetchDefaultTeamMembersCompleted)
    .on(TicketingActions.FETCH_DEFAULT_TEAM_MEMBERS_ERROR, fetchDefaultTeamMembersError)
    .on(TicketingActions.CREATE_TEAM_START, createTeamStart)
    .on(TicketingActions.CREATE_TEAM_COMPLETED, createTeamCompleted)
    .on(TicketingActions.CREATE_TEAM_ERROR, createTeamError)
    .on(TicketingActions.FETCH_TEAM_DETAIL_START, fetchTeamDetailStart)
    .on(TicketingActions.FETCH_TEAM_DETAIL_COMPLETED, fetchTeamDetailCompleted)
    .on(TicketingActions.FETCH_TEAM_DETAIL_ERROR, fetchTeamDetailError)
    .on(TicketingActions.REMOVE_TEAM_MEMBER_START, removeTeamMemberStart)
    .on(TicketingActions.REMOVE_TEAM_MEMBER_COMPLETED, removeTeamMemberCompleted)
    .on(TicketingActions.REMOVE_TEAM_MEMBER_ERROR, removeTeamMemberError)
    .on(TicketingActions.ADD_TEAM_MEMBERS_START, addTeamMembersStart)
    .on(TicketingActions.ADD_TEAM_MEMBERS_COMPLETED, addTeamMembersCompleted)
    .on(TicketingActions.ADD_TEAM_MEMBERS_ERROR, addTeamMembersError)
    .on(TicketingActions.RENAME_TEAM_START, renameTeamStart)
    .on(TicketingActions.RENAME_TEAM_COMPLETED, renameTeamCompleted)
    .on(TicketingActions.RENAME_TEAM_ERROR, renameTeamError)
    .on(TicketingActions.ASSIGN_TEAM_MANAGER_START, assignTeamManagerStart)
    .on(TicketingActions.ASSIGN_TEAM_MANAGER_COMPLETED, assignTeamManagerCompleted)
    .on(TicketingActions.ASSIGN_TEAM_MANAGER_ERROR, assignTeamManagerError)
    .on(TicketingActions.REMOVE_TEAM_MANAGER_START, removeTeamManagerStart)
    .on(TicketingActions.REMOVE_TEAM_MANAGER_COMPLETED, removeTeamManagerCompleted)
    .on(TicketingActions.REMOVE_TEAM_MANAGER_ERROR, removeTeamManagerError)
    .build()
);
